import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Nav, Spinner } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { Offcanvas } from 'react-bootstrap';
import { Modal, Form } from 'react-bootstrap';
import BottomNav from '../components/BottomNav';
import CandlestickChart from '../components/CandlestickChart';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
} from 'chart.js';

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
);

const ExchangePage = () => {
  const [showTradeModal, setShowTradeModal] = useState(false);
  const [tradeType, setTradeType] = useState('Buy');
  const [tradeAmount, setTradeAmount] = useState('');
  const [orderType, setOrderType] = useState('MARKET'); // 'LIMIT' or 'MARKET'
  const [limitPrice, setLimitPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [walletBalances, setWalletBalances] = useState(null);
  const [priceData, setPriceData] = useState([
    104800, 104750, 104700, 104650, 104600,
  ]);
  const [activeTab, setActiveTab] = useState('pending');
  const [, setCurrentPrice] = useState(104655.13);
  const [coins, setCoins] = useState([]);
  const [selectedCoin, setSelectedCoin] = useState(() => {
    const savedCoin = localStorage.getItem('selectedExchangeCoin');
    if (savedCoin) {
      try {
        return JSON.parse(savedCoin);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [showCoinList, setShowCoinList] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchTerm, setSearchTerm] = useState('');
  const [chartType, setChartType] = useState('candlestick'); // 'line' or 'candlestick'
  const [pendingOrders, setPendingOrders] = useState([]);
  const [latestTransactions, setLatestTransactions] = useState([]);
  const [orderBook, setOrderBook] = useState({ bids: [], asks: [] });
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch coins from backend
  useEffect(() => {
    const fetchCoins = async () => {
      try {
        const response = await api.get('/api/trading/prices');

        const prices = response.data?.data?.prices || [];

        const formattedCoins = prices.map((coin) => ({
          symbol: `${coin.symbol}/USDT`,
          price: coin.currentPrice,
          change: coin.priceChange24h,
          name: coin.name,
          quantity: 0,
        }));

        setCoins(formattedCoins);

        if (!selectedCoin && formattedCoins.length > 0) {
          setSelectedCoin(formattedCoins[0]);
        }
      } catch (error) {}
    };

    fetchCoins();
    const interval = setInterval(fetchCoins, 30000);
    return () => clearInterval(interval);
  }, [selectedCoin]);

  useEffect(() => {
    localStorage.setItem('selectedExchangeCoin', JSON.stringify(selectedCoin));
  }, [selectedCoin]);

  // Fetch pending orders
  useEffect(() => {
    const fetchPendingOrders = async () => {
      if (activeTab !== 'pending') return;

      setLoadingOrders(true);
      try {
        const response = await api.get('/api/orderbook/orders/pending');
        if (response.data.success) {
          setPendingOrders(response.data.data.orders || []);
        }
      } catch (error) {
        console.error('Error fetching pending orders:', error);
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchPendingOrders();
  }, [activeTab]);

  // Fetch latest transactions
  useEffect(() => {
    const fetchLatestTransactions = async () => {
      if (activeTab !== 'latest') return;

      setLoadingOrders(true);
      try {
        const response = await api.get('/api/orderbook/orders/all');
        if (response.data.success) {
          setLatestTransactions(response.data.data.orders || []);
        }
      } catch (error) {
        console.error('Error fetching latest transactions:', error);
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchLatestTransactions();
  }, [activeTab]);

  // Fetch order book
  useEffect(() => {
    const fetchOrderBook = async () => {
      if (activeTab !== 'record' || !selectedCoin) return;

      setLoadingOrders(true);
      try {
        const pair = selectedCoin?.symbol || 'BTC/USDT';
        const response = await api.get(
          `/api/orderbook/market/depth?pair=${pair}&depth=20`,
        );
        if (response.data.success) {
          setOrderBook({
            bids: response.data.data.bids || [],
            asks: response.data.data.asks || [],
          });
        }
      } catch (error) {
        console.error('Error fetching order book:', error);
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchOrderBook();
  }, [activeTab, selectedCoin]);

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const response = await api.get('/api/trading/wallet/balances');
        if (response.data.success) {
          setWalletBalances(response.data.data);
        }
      } catch (error) {}
    };

    fetchWalletData();
  }, [selectedCoin]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPriceData((prev) => {
        const newPrice = +(
          prev[prev.length - 1] +
          (Math.random() * 20 - 10)
        ).toFixed(2);
        return [...prev.slice(1), newPrice];
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setCurrentPrice(priceData[priceData.length - 1]);
  }, [priceData]);

  const getAvailableBalance = () => {
    if (!walletBalances?.real) return 0;

    if (tradeType === 'Buy') {
      return walletBalances.real.USDT?.availableAssets || 0;
    } else {
      const baseCurrency = selectedCoin?.symbol?.split('/')[0] || 'BTC';
      return walletBalances.real[baseCurrency]?.availableAssets || 0;
    }
  };

  const handlePercentageClick = (percentage) => {
    const availableBalance = getAvailableBalance();
    const amount = availableBalance * (percentage / 100);
    setTradeAmount(amount.toFixed(2));
  };

  const handleExchange = async () => {
    if (!tradeAmount || parseFloat(tradeAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (orderType === 'LIMIT' && (!limitPrice || parseFloat(limitPrice) <= 0)) {
      toast.error('Please enter a valid limit price');
      return;
    }

    setLoading(true);
    try {
      const baseCurrency = selectedCoin?.symbol?.split('/')[0] || 'BTC';
      const pair = selectedCoin?.symbol || 'BTC/USDT';
      const quoteCurrency = 'USDT';
      const side = tradeType === 'Buy' ? 'BUY' : 'SELL';

      const requestBody = {
        pair: pair,
        baseCurrency: baseCurrency,
        quoteCurrency: quoteCurrency,
        side: side,
        orderType: orderType,
        amount: parseFloat(tradeAmount),
      };

      // Add price only for LIMIT orders
      if (orderType === 'LIMIT') {
        requestBody.price = parseFloat(limitPrice);
      }

      const response = await api.post('/api/orderbook/order', requestBody);

      if (response.data.success) {
        toast.success(
          response.data.message || `${side} order placed successfully!`,
        );
        setShowTradeModal(false);
        setTradeAmount('');
        setLimitPrice('');

        const balanceResponse = await api.get('/api/trading/wallet/balances');
        if (balanceResponse.data.success) {
          setWalletBalances(balanceResponse.data.data);
        }
      } else {
        toast.error(response.data.message || 'Order placement failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Order placement failed');
    } finally {
      setLoading(false);
    }
  };

  const chartData = {
    labels: priceData.map((_, i) => i.toString()),
    datasets: [
      {
        label: 'Price',
        data: priceData,
        fill: true,
        borderColor: 'rgba(220, 53, 69, 1)',
        backgroundColor: 'rgba(220, 53, 69, 0.1)',
        tension: 0.3,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { display: false },
      y: { display: false },
    },
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        paddingTop: '40px',
        paddingBottom: '100px',
        paddingLeft: 'clamp(0.75rem, 2vw, 1.5rem)',
        paddingRight: 'clamp(0.75rem, 2vw, 1.5rem)',
      }}
    >
      {!selectedCoin || coins.length === 0 ? (
        <div className='text-center mt-5'>
          <Spinner
            animation='border'
            variant='primary'
            style={{ width: '3rem', height: '3rem' }}
          />
          <p className='mt-3 text-muted'>Loading...</p>
        </div>
      ) : (
        <>
          <div
            className='text-center d-flex align-items-center justify-content-center'
            onClick={() => setShowCoinList(true)}
            style={{ cursor: 'pointer', marginBottom: '12px' }}
          >
            <img
              src={require(
                `../assets/coins/${selectedCoin.symbol
                  .split('/')[0]
                  .toLowerCase()}.png`,
              )}
              alt={selectedCoin.symbol.split('/')[0]}
              style={{
                width: 'clamp(24px, 5vw, 32px)',
                height: 'clamp(24px, 5vw, 32px)',
              }}
              className='me-2'
            />
            <div
              style={{
                fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                fontWeight: 'bold',
              }}
            >
              {selectedCoin.symbol}
            </div>
          </div>
          <div
            className='text-center text-danger fw-bold'
            style={{ fontSize: 'clamp(1.5rem, 5vw, 2rem)' }}
          >
            {selectedCoin?.price?.toFixed(2) || '0.00'}
          </div>
          <div
            className={`text-center mb-2 ${
              selectedCoin?.change >= 0 ? 'text-success' : 'text-danger'
            }`}
            style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1rem)' }}
          >
            {selectedCoin?.change >= 0 ? '+' : ''}
            {selectedCoin?.change?.toFixed(2) || '0.00'}%
          </div>

          {/* Info Row */}
          <Row className='text-center mb-3 g-2'>
            <Col>
              <small style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)' }}>
                24hHigh
              </small>
              <div
                style={{
                  fontSize: 'clamp(0.8rem, 2.2vw, 0.95rem)',
                  fontWeight: '600',
                }}
              >
                0.00
              </div>
            </Col>
            <Col>
              <small style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)' }}>
                24hTrade. Turnover
              </small>
              <div
                style={{
                  fontSize: 'clamp(0.8rem, 2.2vw, 0.95rem)',
                  fontWeight: '600',
                }}
              >
                {(selectedCoin?.price * 1000)?.toFixed(2) || '0.00'}
              </div>
            </Col>
            <Col>
              <small style={{ fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)' }}>
                24hMinimum
              </small>
              <div
                style={{
                  fontSize: 'clamp(0.8rem, 2.2vw, 0.95rem)',
                  fontWeight: '600',
                }}
              >
                {(selectedCoin?.price * 0.95)?.toFixed(2) || '0.00'}
              </div>
            </Col>
          </Row>

          {/* Time Tabs */}
          <div className='d-flex justify-content-between mb-2 gap-1'>
            {['1M', '5M', '15M', '30M', '1H', '1D', '7D'].map((t) => (
              <Button
                key={t}
                variant='light'
                style={{
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  padding:
                    'clamp(0.3rem, 1.5vw, 0.5rem) clamp(0.4rem, 2vw, 0.6rem)',
                  flex: '1',
                  minWidth: '0',
                }}
              >
                {t}
              </Button>
            ))}
          </div>

          {/* Chart */}
          <div
            className='bg-light rounded mb-3'
            style={{
              padding: 'clamp(0.5rem, 2vw, 1rem)',
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            }}
          >
            {/* Chart Type Toggle */}
            <div className='d-flex justify-content-end mb-2 gap-2'>
              <Button
                size='sm'
                variant={chartType === 'line' ? 'primary' : 'outline-secondary'}
                onClick={() => setChartType('line')}
                style={{
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  padding:
                    'clamp(0.3rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)',
                  borderRadius: '6px',
                }}
              >
                <i className='bi bi-graph-up me-1'></i>
                Line
              </Button>
              <Button
                size='sm'
                variant={
                  chartType === 'candlestick' ? 'primary' : 'outline-secondary'
                }
                onClick={() => setChartType('candlestick')}
                style={{
                  fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                  padding:
                    'clamp(0.3rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)',
                  borderRadius: '6px',
                }}
              >
                <i className='bi bi-bar-chart me-1'></i>
                Candlestick
              </Button>
            </div>

            {/* Conditional Chart Rendering */}
            <div style={{ height: isMobile ? '260px' : '440px' }}>
              {chartType === 'line' ? (
                <Line
                  key={isMobile ? 'mobile-chart' : 'desktop-chart'}
                  data={chartData}
                  options={chartOptions}
                />
              ) : (
                <CandlestickChart
                  coinSymbol={selectedCoin?.symbol?.split('/')[0] || 'BTC'}
                  height={isMobile ? 230 : 405}
                />
              )}
            </div>
          </div>

          {/* Buy/Sell Buttons */}
          <Row className='mb-3 g-2'>
            <Col xs={6}>
              <Button
                className='w-100 fw-bold'
                variant='success'
                style={{
                  padding: 'clamp(0.6rem, 2.5vw, 0.8rem)',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                  borderRadius: '8px',
                }}
                onClick={() => {
                  setTradeType('Buy');
                  setTradeAmount('');
                  if (orderType === 'LIMIT') {
                    setLimitPrice(selectedCoin?.price?.toFixed(2) || '');
                  }
                  setShowTradeModal(true);
                }}
              >
                Buy
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                className='w-100 fw-bold'
                variant='danger'
                style={{
                  padding: 'clamp(0.6rem, 2.5vw, 0.8rem)',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                  borderRadius: '8px',
                }}
                onClick={() => {
                  setTradeType('Sell');
                  setTradeAmount('');
                  if (orderType === 'LIMIT') {
                    setLimitPrice(selectedCoin?.price?.toFixed(2) || '');
                  }
                  setShowTradeModal(true);
                }}
              >
                Sell
              </Button>
            </Col>
          </Row>

          {/* Order Tabs */}
          <Nav
            variant='pills'
            className='justify-content-center mb-3 flex-wrap gap-1'
          >
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'pending'}
                onClick={() => setActiveTab('pending')}
                style={{
                  padding:
                    'clamp(0.5rem, 2vw, 0.7rem) clamp(0.7rem, 2.5vw, 1rem)',
                  fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
                  fontWeight: '500',
                }}
              >
                Order pending
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'latest'}
                onClick={() => setActiveTab('latest')}
                style={{
                  padding:
                    'clamp(0.5rem, 2vw, 0.7rem) clamp(0.7rem, 2.5vw, 1rem)',
                  fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
                  fontWeight: '500',
                }}
              >
                Latest transaction
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'record'}
                onClick={() => setActiveTab('record')}
                style={{
                  padding:
                    'clamp(0.5rem, 2vw, 0.7rem) clamp(0.7rem, 2.5vw, 1rem)',
                  fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
                  fontWeight: '500',
                }}
              >
                Order Record
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Conditional Tab Content */}
          <div
            className='bg-white rounded shadow-sm p-3 mb-3'
            style={{
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              minHeight: '150px',
            }}
          >
            {/* Order Pending Tab */}
            {activeTab === 'pending' && (
              <div>
                <div
                  className='mb-2'
                  style={{
                    fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
                    fontWeight: '600',
                  }}
                >
                  Order Pending
                </div>
                {loadingOrders ? (
                  <div className='text-center py-4'>
                    <Spinner animation='border' size='sm' variant='primary' />
                  </div>
                ) : (
                  <>
                    <div className='table-responsive'>
                      <table className='table table-sm'>
                        <thead>
                          <tr
                            style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                          >
                            <th>Pair</th>
                            <th>Type</th>
                            <th>Side</th>
                            <th>Price</th>
                            <th>Amount</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody
                          style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                        >
                          {pendingOrders.length > 0 ? (
                            pendingOrders.map((order) => (
                              <tr key={order.id}>
                                <td>{order.pair}</td>
                                <td>{order.type}</td>
                                <td
                                  className={
                                    order.side === 'BUY'
                                      ? 'text-success'
                                      : 'text-danger'
                                  }
                                >
                                  {order.side}
                                </td>
                                <td>
                                  {order.price > 0
                                    ? order.price.toFixed(2)
                                    : 'Market'}
                                </td>
                                <td>{order.amount}</td>
                                <td className='text-warning'>{order.status}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan='6'
                                className='text-center text-muted'
                              >
                                No pending orders
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {pendingOrders.length > 0 && (
                      <div
                        className='text-center text-muted mt-3'
                        style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                      >
                        No more pending orders
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Latest Transaction Tab */}
            {activeTab === 'latest' && (
              <div>
                <div
                  className='mb-2'
                  style={{
                    fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
                    fontWeight: '600',
                  }}
                >
                  Latest Transactions
                </div>
                {loadingOrders ? (
                  <div className='text-center py-4'>
                    <Spinner animation='border' size='sm' variant='primary' />
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table table-sm'>
                      <thead>
                        <tr
                          style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                        >
                          <th>Time</th>
                          <th>Price</th>
                          <th>Amount</th>
                          <th>Side</th>
                        </tr>
                      </thead>
                      <tbody style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}>
                        {latestTransactions.length > 0 ? (
                          latestTransactions.map((transaction) => (
                            <tr key={transaction.id}>
                              <td>
                                {new Date(
                                  transaction.createdAt,
                                ).toLocaleTimeString()}
                              </td>
                              <td>
                                {transaction.price > 0
                                  ? transaction.price.toFixed(4)
                                  : 'Market'}
                              </td>
                              <td>
                                {transaction.amount}{' '}
                                {transaction.pair.split('/')[0]}
                              </td>
                              <td
                                className={
                                  transaction.side === 'BUY'
                                    ? 'text-success'
                                    : 'text-danger'
                                }
                              >
                                {transaction.side === 'BUY' ? 'Buy' : 'Sell'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan='4' className='text-center text-muted'>
                              No transactions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Order Record Tab */}
            {activeTab === 'record' && (
              <div>
                <div
                  className='mb-3'
                  style={{
                    fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)',
                    fontWeight: '600',
                  }}
                >
                  Order Book
                </div>
                {loadingOrders ? (
                  <div className='text-center py-4'>
                    <Spinner animation='border' size='sm' variant='primary' />
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table table-sm'>
                      <thead>
                        <tr
                          style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                        >
                          <th>Bid Price (Buy)</th>
                          <th>Bid Amount</th>
                          <th>Ask Amount</th>
                          <th>Ask Price (Sell)</th>
                        </tr>
                      </thead>
                      <tbody style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}>
                        {orderBook.bids.length > 0 ||
                        orderBook.asks.length > 0 ? (
                          Array.from({
                            length: Math.max(
                              orderBook.bids.length,
                              orderBook.asks.length,
                            ),
                          }).map((_, index) => (
                            <tr key={index}>
                              <td className='text-success'>
                                {orderBook.bids[index]?.price?.toFixed(2) ||
                                  '-'}
                              </td>
                              <td>{orderBook.bids[index]?.amount || '-'}</td>
                              <td>{orderBook.asks[index]?.amount || '-'}</td>
                              <td className='text-danger'>
                                {orderBook.asks[index]?.price?.toFixed(2) ||
                                  '-'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan='4' className='text-center text-muted'>
                              No order book data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <BottomNav />
          {/* Coin Switch Side Popup */}
          <Offcanvas
            show={showCoinList}
            onHide={() => setShowCoinList(false)}
            placement='end'
            style={{ width: isMobile ? '85%' : '400px' }}
          >
            <Offcanvas.Header
              closeButton
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              <Offcanvas.Title
                style={{
                  fontSize: 'clamp(1rem, 3vw, 1.3rem)',
                  fontWeight: '600',
                }}
              >
                Select Coin
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body style={{ background: '#f8f9fa' }}>
              <div className='position-relative mb-3'>
                <i
                  className='bi bi-search position-absolute'
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    fontSize: '1.1rem',
                  }}
                ></i>
                <Form.Control
                  type='text'
                  placeholder='Search coin...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '40px',
                    borderRadius: '10px',
                    border: '2px solid #e9ecef',
                    fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
                  }}
                />
              </div>
              {coins
                .filter((coin) =>
                  coin.symbol.toLowerCase().includes(searchTerm.toLowerCase()),
                )
                .map((coin) => (
                  <div
                    key={coin.symbol}
                    className='d-flex justify-content-between align-items-center p-3 mb-2 bg-white rounded shadow-sm'
                    onClick={() => {
                      setSelectedCoin(coin);
                      setShowCoinList(false);
                      setSearchTerm('');
                    }}
                    style={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(5px)';
                      e.currentTarget.style.boxShadow =
                        '0 4px 12px rgba(102, 126, 234, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                      e.currentTarget.style.boxShadow =
                        '0 2px 4px rgba(0,0,0,0.05)';
                    }}
                  >
                    <div>
                      <strong
                        style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}
                      >
                        {coin.symbol}
                      </strong>
                      <br />
                      <small
                        className='text-muted'
                        style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                      >
                        {coin.quantity
                          ? coin.quantity.toLocaleString()
                          : coin.name}
                      </small>
                    </div>
                    <div className='text-end'>
                      <div
                        style={{
                          fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
                          fontWeight: '600',
                          color: '#333',
                        }}
                      >
                        {coin.price.toFixed(2)}
                      </div>
                      <div
                        className={
                          coin.change >= 0 ? 'text-success' : 'text-danger'
                        }
                        style={{
                          fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                          fontWeight: '600',
                        }}
                      >
                        {coin.change >= 0 ? '+' : ''}
                        {Number(coin.change).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                ))}
            </Offcanvas.Body>
          </Offcanvas>
          <Modal
            show={showTradeModal}
            onHide={() => setShowTradeModal(false)}
            centered
            size={isMobile ? '' : 'md'}
          >
            <Modal.Header
              closeButton
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
              }}
            >
              <Modal.Title style={{ fontSize: 'clamp(1rem, 3vw, 1.3rem)' }}>
                Order Confirmation
              </Modal.Title>
            </Modal.Header>
            <Modal.Body
              style={{
                background: '#f8f9fa',
                padding: 'clamp(1rem, 3vw, 1.5rem)',
              }}
            >
              <Form>
                {/* Trading Pair Info */}
                <div
                  className='mb-3 p-3'
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    border: '2px solid #667eea',
                  }}
                >
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <span
                      className='text-muted'
                      style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                    >
                      Trading Pair
                    </span>
                    <span
                      className='fw-bold'
                      style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)' }}
                    >
                      {selectedCoin?.symbol || 'BTC/USDT'}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between align-items-center'>
                    <span
                      className='text-muted'
                      style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                    >
                      Market Price
                    </span>
                    <span
                      className='fw-bold text-primary'
                      style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)' }}
                    >
                      {selectedCoin?.price?.toFixed(2) || '0.00'} USDT
                    </span>
                  </div>
                </div>

                {/* Order Type Dropdown */}
                <Form.Group className='mb-3'>
                  <Form.Label
                    className='fw-semibold'
                    style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                  >
                    Order Type
                  </Form.Label>
                  <Form.Select
                    value={orderType}
                    onChange={(e) => {
                      setOrderType(e.target.value);
                      if (e.target.value === 'MARKET') {
                        setLimitPrice('');
                      } else if (e.target.value === 'LIMIT') {
                        setLimitPrice(selectedCoin?.price?.toFixed(2) || '');
                      }
                    }}
                    style={{
                      fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
                      padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                      borderRadius: '8px',
                      fontWeight: '600',
                    }}
                  >
                    <option value='MARKET'>Market Order</option>
                    <option value='LIMIT'>Limit Order</option>
                  </Form.Select>
                  <Form.Text className='text-muted'>
                    {orderType === 'MARKET'
                      ? 'Execute immediately at current market price'
                      : 'Set your own price for the order'}
                  </Form.Text>
                </Form.Group>

                {/* Limit Price Field (conditional) */}
                {orderType === 'LIMIT' && (
                  <Form.Group className='mb-3'>
                    <Form.Label
                      className='fw-semibold'
                      style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                    >
                      Limit Price (USDT)
                    </Form.Label>
                    <Form.Control
                      type='number'
                      placeholder='Enter your limit price'
                      value={limitPrice}
                      onChange={(e) => setLimitPrice(e.target.value)}
                      style={{
                        fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
                        padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                        borderRadius: '8px',
                      }}
                    />
                    <Form.Text className='text-muted'>
                      Your order will execute when price reaches this level
                    </Form.Text>
                  </Form.Group>
                )}

                {/* Amount Field */}
                <Form.Group className='mb-3'>
                  <Form.Label
                    className='fw-semibold'
                    style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                  >
                    Amount ({selectedCoin?.symbol?.split('/')[0] || 'BTC'})
                  </Form.Label>
                  <Form.Control
                    type='number'
                    placeholder={`Enter ${
                      selectedCoin?.symbol?.split('/')[0] || 'BTC'
                    } amount`}
                    value={tradeAmount}
                    onChange={(e) => setTradeAmount(e.target.value)}
                    style={{
                      fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
                      padding: 'clamp(0.6rem, 2vw, 0.8rem)',
                      borderRadius: '8px',
                    }}
                  />
                  <Form.Text className='text-muted'>
                    How much {selectedCoin?.symbol?.split('/')[0] || 'BTC'} to{' '}
                    {tradeType.toLowerCase()}
                  </Form.Text>
                </Form.Group>

                {/* Available Balance */}
                <div
                  className='mb-3 p-3'
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <span
                      className='text-muted'
                      style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                    >
                      Available Balance
                    </span>
                    <span
                      className='fw-bold text-success'
                      style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1rem)' }}
                    >
                      {getAvailableBalance().toFixed(2)}{' '}
                      {tradeType === 'Buy'
                        ? 'USDT'
                        : selectedCoin?.symbol?.split('/')[0] || 'BTC'}
                    </span>
                  </div>
                  <div className='d-flex justify-content-between gap-2'>
                    {['20%', '50%', '75%', '100%'].map((p) => (
                      <div
                        key={p}
                        className='flex-fill text-center'
                        onClick={() => handlePercentageClick(parseInt(p))}
                        style={{
                          fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                          padding: 'clamp(0.4rem, 2vw, 0.6rem)',
                          border: '1px solid #667eea',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          color: '#667eea',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#667eea';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.color = '#667eea';
                        }}
                      >
                        {p}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Summary */}
                {tradeAmount && parseFloat(tradeAmount) > 0 && (
                  <div
                    className='mb-3 p-3'
                    style={{
                      background:
                        'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                      borderRadius: '8px',
                      border: '1px solid #667eea50',
                    }}
                  >
                    <div
                      className='fw-semibold mb-2'
                      style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                    >
                      Order Summary
                    </div>
                    <div className='d-flex justify-content-between mb-1'>
                      <span
                        className='text-muted'
                        style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                      >
                        Side:
                      </span>
                      <span
                        className={`fw-semibold ${
                          tradeType === 'Buy' ? 'text-success' : 'text-danger'
                        }`}
                      >
                        {tradeType.toUpperCase()}
                      </span>
                    </div>
                    <div className='d-flex justify-content-between mb-1'>
                      <span
                        className='text-muted'
                        style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                      >
                        Type:
                      </span>
                      <span className='fw-semibold'>{orderType}</span>
                    </div>
                    <div className='d-flex justify-content-between mb-1'>
                      <span
                        className='text-muted'
                        style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                      >
                        Amount:
                      </span>
                      <span className='fw-semibold'>
                        {parseFloat(tradeAmount).toFixed(2)}{' '}
                        {selectedCoin?.symbol?.split('/')[0] || 'BTC'}
                      </span>
                    </div>
                    {orderType === 'LIMIT' && limitPrice && (
                      <div className='d-flex justify-content-between mb-1'>
                        <span
                          className='text-muted'
                          style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                        >
                          Price:
                        </span>
                        <span className='fw-semibold'>
                          {parseFloat(limitPrice).toFixed(2)} USDT
                        </span>
                      </div>
                    )}
                    <div className='d-flex justify-content-between pt-2 border-top mt-2'>
                      <span
                        className='fw-semibold'
                        style={{ fontSize: 'clamp(0.8rem, 2vw, 0.9rem)' }}
                      >
                        Total:
                      </span>
                      <span
                        className='fw-bold text-primary'
                        style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1rem)' }}
                      >
                        {orderType === 'MARKET'
                          ? `≈ ${(
                              parseFloat(tradeAmount) *
                              (selectedCoin?.price || 0)
                            ).toFixed(2)}`
                          : (
                              parseFloat(tradeAmount) *
                              parseFloat(limitPrice || 0)
                            ).toFixed(2)}{' '}
                        USDT
                      </span>
                    </div>
                  </div>
                )}

                <Button
                  variant={tradeType === 'Buy' ? 'success' : 'danger'}
                  className='w-100 fw-bold'
                  onClick={handleExchange}
                  disabled={
                    loading ||
                    !tradeAmount ||
                    parseFloat(tradeAmount) <= 0 ||
                    (orderType === 'LIMIT' &&
                      (!limitPrice || parseFloat(limitPrice) <= 0))
                  }
                  style={{
                    fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                    padding: 'clamp(0.7rem, 2vw, 0.9rem)',
                    borderRadius: '8px',
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner animation='border' size='sm' className='me-2' />
                      Processing...
                    </>
                  ) : (
                    `Place ${orderType} ${tradeType.toUpperCase()} Order`
                  )}
                </Button>
              </Form>
            </Modal.Body>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default ExchangePage;
